import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, Zap, MessageSquare } from 'lucide-react';
import { fadeUp, staggerContainer, defaultViewport } from '../components/ui/AnimationVariants';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        message: ''
      });
    }, 1500);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      details: ['(978) 833-2933'],
      description: 'Available Monday-Friday 8AM-5PM'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['info@bprelectric.com', 'projects@bprelectric.com'],
      description: 'We respond within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Office',
      details: ['580 Main Street St 10', 'Bolton, MA 01740'],
      description: 'Visit us for consultation'
    },
  ];

  const projectTypes = [
    'Commercial Electrical Design',
    'Electrical Installation',
    'Solar Power Solutions',
    'Neighborhood Infrastructure',
    'Luxury Residential',
    'Maintenance Services',
    'Other'
  ];

  return (
    <div>
      {/* Hero */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        animate="visible"
        className="pt-10 pb-8 lg:pt-14 lg:pb-10 border-b border-navy-600 bg-navy-950"
      >
        <div className="container-custom">
          <motion.p variants={fadeUp} className="eyebrow">Contact Us</motion.p>
          <motion.h1 variants={fadeUp} className="font-heading text-h1 text-white mt-3 max-w-3xl">
            Contact Us
          </motion.h1>
          <motion.p variants={fadeUp} className="text-slate-400 text-base leading-relaxed mt-4 max-w-2xl">
            Get in touch with our expert team for your commercial electrical project needs. We're here to help bring your vision to life.
          </motion.p>
        </div>
      </motion.section>

      {/* Contact Info Cards */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="py-0 bg-navy-900 border-b border-navy-600"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-navy-600">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                className="flex items-start gap-4 px-6 py-6"
              >
                <info.icon className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-heading text-white uppercase text-sm tracking-widest mb-1">{info.title}</h3>
                  {info.details.map((detail, detailIndex) => (
                    <p key={detailIndex} className="text-white text-sm font-medium leading-snug">{detail}</p>
                  ))}
                  <p className="text-slate-400 text-xs mt-1">{info.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <motion.section
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={defaultViewport}
        className="section-standard bg-navy-950"
      >
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <motion.div variants={fadeUp}>
              <p className="eyebrow">Start Your Project</p>
              <h2 className="font-heading text-h2 text-white mt-4">
                Start Your Project
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mt-6">
                Fill out the form below and our team will get back to you within 24 hours to discuss your electrical project requirements.
              </p>

              <div className="bg-navy-700 border border-navy-600 p-8 mt-8">
                <div className="flex items-center mb-4">
                  <Clock className="h-5 w-5 text-primary-500 mr-3" />
                  <h3 className="font-heading text-white uppercase text-lg">Business Hours</h3>
                </div>
                <div className="space-y-2 text-slate-400 text-sm">
                  <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                  <p>Saturday: 9:00 AM - 2:00 PM</p>
                  <p>Sunday: Closed</p>
                  <p className="font-heading uppercase text-primary-500 tracking-wide mt-3">24/7 Emergency Services Available</p>
                </div>
              </div>

              <div className="bg-navy-700 border border-navy-600 border-l-2 border-l-primary-500 p-8 mt-4">
                <div className="flex items-center mb-4">
                  <Zap className="h-5 w-5 text-primary-500 mr-3" />
                  <h3 className="font-heading text-white uppercase text-lg">Why Choose BPR Electric?</h3>
                </div>
                <ul className="space-y-3">
                  <li className="flex items-start text-slate-400 text-sm">
                    <span className="text-primary-500 mr-2">—</span>
                    15+ years of commercial electrical expertise
                  </li>
                  <li className="flex items-start text-slate-400 text-sm">
                    <span className="text-primary-500 mr-2">—</span>
                    Licensed, insured, and certified professionals
                  </li>
                  <li className="flex items-start text-slate-400 text-sm">
                    <span className="text-primary-500 mr-2">—</span>
                    Specialized in multi-million dollar projects
                  </li>
                  <li className="flex items-start text-slate-400 text-sm">
                    <span className="text-primary-500 mr-2">—</span>
                    Comprehensive warranty and support
                  </li>
                </ul>
              </div>
            </motion.div>

            <motion.div variants={fadeUp}>
              <div className="bg-navy-800 border border-navy-600 p-8">
                {submitStatus === 'success' ? (
                  <div className="text-center py-12">
                    <div className="border border-navy-600 p-4 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                      <MessageSquare className="h-10 w-10 text-primary-500" />
                    </div>
                    <h3 className="font-heading text-white uppercase text-h3 mb-4">Thank You!</h3>
                    <p className="text-slate-400 mb-6">
                      Your message has been sent successfully. Our team will contact you within 24 hours.
                    </p>
                    <button
                      onClick={() => setSubmitStatus('')}
                      className="btn-primary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Phone
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="(978) 833-2933"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                          Company
                        </label>
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors placeholder-slate-400"
                          placeholder="Your company name"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                        Project Type *
                      </label>
                      <select
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors"
                      >
                        <option value="">Select a project type</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-heading uppercase tracking-widest text-slate-400 mb-2">
                        Project Details *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        className="w-full px-4 py-3 bg-navy-700 border border-navy-600 text-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none transition-colors resize-none placeholder-slate-400"
                        placeholder="Tell us about your project requirements, timeline, and any specific needs..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin h-5 w-5 border-b-2 border-white mr-3"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Emergency Section */}
      <section className="bg-red-900 border-t border-red-800 py-12">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-white uppercase text-3xl mb-4">
              24/7 Emergency Electrical Services
            </h2>
            <p className="text-red-200 text-lg mb-6">
              For emergency electrical issues, call us immediately for rapid response service.
            </p>
            <div className="flex items-center justify-center space-x-2 text-white text-xl font-heading uppercase tracking-wide">
              <Phone className="h-6 w-6" />
              <span>Contact Assigned Construction Manager</span>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
